<template>
  <!-- Two Selects instead of a calendar: a date the user already knows is quicker and far more
       screen-reader friendly to pick from lists than to navigate to in a grid. Each combobox is named
       by the group's label plus its own visible floating label ("In what month and year were you born? Month").
       Every month and year is selectable, including years too recent for minimumAge: rather than hiding or
       disabling them without saying why, a too-young pick is kept and reported through the tooYoung event so
       the parent can show an error explaining it. The year list still opens on the latest allowed year (see
       onYearBeforeShow and scrollLatestYearToTop), with the too-recent years above it.
       append-to="self" keeps each listbox right after its combobox in the DOM (teleported to <body>,
       Narrator's scan mode skips past it); focusout on the group (rather than each Select's blur) fires
       only once focus leaves both. -->
  <div
    role="group"
    :aria-labelledby="props.labelledBy"
    class="w-full flex flex-row flex-wrap gap-4"
    @focusout="onFocusout"
  >
    <FloatLabel
      variant="on"
      class="w-40"
    >
      <SelectComponent
        :input-id="monthControlId"
        v-model="month"
        :aria-labelledby="`${props.labelledBy} ${monthLabelId}`"
        append-to="self"
        :pt="selectPt"
        :options="monthOptions"
        option-label="label"
        option-value="value"
        :invalid="props.invalid"
        class="w-full"
        :data-test="`${props.name}-month`"
      />
      <label
        :id="monthLabelId"
        :for="monthControlId"
      >{{ t('form.fields.birthMonth') }}</label>
    </FloatLabel>
    <FloatLabel
      variant="on"
      class="w-40"
    >
      <SelectComponent
        ref="yearSelect"
        :input-id="yearControlId"
        v-model="year"
        :aria-labelledby="`${props.labelledBy} ${yearLabelId}`"
        append-to="self"
        :pt="yearSelectPt"
        :options="yearOptions"
        option-label="label"
        option-value="value"
        :invalid="props.invalid"
        class="w-full"
        :data-test="`${props.name}-year`"
        @before-show="onYearBeforeShow"
      />
      <label
        :id="yearLabelId"
        :for="yearControlId"
      >{{ t('form.fields.birthYear') }}</label>
    </FloatLabel>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch, type PropType } from 'vue';

const t: any = inject('t');

const props = defineProps({
  // The 1st of the chosen month, or null until both month and year are picked. Can be younger than
  // minimumAge - the tooYoung event reports that so the parent can flag it.
  modelValue: {
    type: Date as PropType<Date | null>,
    default: null,
  },
  // id of the visible label/heading naming the whole field
  labelledBy: {
    type: String,
    required: true,
  },
  // Base id for the month/year comboboxes (`${controlId}-month`, `${controlId}-year`)
  controlId: {
    type: String,
    required: true,
  },
  // Prefix for the month/year data-test hooks (`${name}-month`, `${name}-year`)
  name: {
    type: String,
    required: true,
  },
  // Picks later than today minus this many years are reported as too young
  minimumAge: {
    type: Number,
    default: 0,
  },
  describedBy: {
    type: String,
    default: undefined,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
});

// tooYoung(boolean): whether the current pick - a full date, or just a year that's too recent whatever the
// month - is younger than minimumAge. Emitted when that changes, so the parent can show or clear its error.
const emits = defineEmits(['update:modelValue', 'blur', 'tooYoung']);

// How far back the year list goes from its latest allowed year
const YEAR_RANGE = 120;

const yearSelect = ref<{ focusedOptionIndex: number; $el: HTMLElement } | null>(null);

const monthControlId = computed(() => `${props.controlId}-month`);
const yearControlId = computed(() => `${props.controlId}-year`);
const monthLabelId = computed(() => `${props.labelledBy}-month`);
const yearLabelId = computed(() => `${props.labelledBy}-year`);

// month is 1-12
const month = ref<number | null>(null);
const year = ref<number | null>(null);

// Latest year/month that meets minimumAge: today minus minimumAge. Only its year and month are used, so it's
// built on day 1 - today's day would roll Feb 29 over to March 1 in a non-leap target year, a month too late.
const latestBirthDate = computed(() => {
  const today = new Date();
  return new Date(today.getFullYear() - props.minimumAge, today.getMonth(), 1);
});

// This year down to YEAR_RANGE years before the latest allowed one. The years above the latest allowed one are
// too young but still offered (see the template comment).
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const oldestYear = latestBirthDate.value.getFullYear() - YEAR_RANGE;
  return Array.from({ length: currentYear - oldestYear + 1 }, (_, i) => ({
    label: String(currentYear - i),
    value: currentYear - i,
  }));
});

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' }),
  value: i + 1,
}));

// A year after the latest allowed one is too young whatever the month; in the latest allowed year, only months
// after its cutoff month are. A dob is stored as the 1st of its month, so the cutoff month itself always passes
// isOverMinimumAge.
const isTooYoung = computed(() => {
  if (year.value === null) return false;
  const latestYear = latestBirthDate.value.getFullYear();
  if (year.value > latestYear) return true;
  return year.value === latestYear && month.value !== null && month.value - 1 > latestBirthDate.value.getMonth();
});

// hiddenSelectedMessage: in-flow (append-to="self"), the overlay's hidden role="status" "No selected item"
// span lands in scan-mode reading order as a dead stop; the combobox already announces its value.
// option: a data-test of the option's value so E2E tests can pick options independent of locale.
// overlay.onClick: Select picks an option on mousedown but toggles open/closed on a click reaching its root, and
// drops its reference to the list as soon as the list starts closing. If the press and release land on different
// rows (e.g. the page shifting under the cursor as a too-young error disappears), the click goes to the list
// itself, still on screen mid-close, and Select reopens it. A click inside the list never needs to toggle it.
const selectPt = computed(() => ({
  hiddenSelectedMessage: { 'aria-hidden': 'true' },
  option: ({ context }: { context: { option: { value: number } } }) => ({ 'data-test': context.option.value }),
  label: { 'aria-describedby': props.describedBy },
  overlay: { onClick: (event: MouseEvent) => event.stopPropagation() },
}));

const yearSelectPt = computed(() => ({
  ...selectPt.value,
  overlay: {
    ...selectPt.value.overlay,
    onVnodeMounted: ({ el }: { el: HTMLElement | null }) => scrollLatestYearToTop(el),
  },
}));

function isSameMonth(a: Date | null, b: Date | null) {
  if (!a || !b) return a === b;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

// Follow the model when the parent changes it (e.g. the saved dob loading in after mount). An empty model
// only clears a complete pick - a half-made one (month chosen, no year yet) is what produced it.
watch(
  () => props.modelValue,
  (value) => {
    if (value instanceof Date) {
      month.value = value.getMonth() + 1;
      year.value = value.getFullYear();
    } else if (month.value !== null && year.value !== null) {
      month.value = null;
      year.value = null;
    }
  },
  { immediate: true },
);

watch([month, year], ([newMonth, newYear]) => {
  const value = newMonth !== null && newYear !== null ? new Date(newYear, newMonth - 1, 1) : null;
  if (!isSameMonth(value, props.modelValue)) emits('update:modelValue', value);
});

// Declared after the model watcher so a parent gets a new date before hearing whether it's too young
watch(isTooYoung, (tooYoung) => emits('tooYoung', tooYoung));

// With no year picked yet, open the year list on the latest allowed year rather than the too-recent ones at the
// top. Select keeps a focused option index that's already set when it shows (and resets it to -1 on hide), so
// setting it here makes keyboard and screen reader users start on that year too; ArrowUp reaches the newer ones.
function onYearBeforeShow() {
  if (year.value !== null || !yearSelect.value) return;
  yearSelect.value.focusedOptionIndex = yearOptions.value.findIndex(
    (option) => option.value === latestBirthDate.value.getFullYear(),
  );
}

// Select scrolls the focused option into view only as far as needed, which would leave the latest allowed year
// at the bottom of the list. Scroll it to the top instead, so the too-recent years are above it, off-screen until
// scrolled to. Done as the list mounts - before it's painted, and before Select's own scroll, which then has
// nothing to do - so it doesn't visibly jump.
function scrollLatestYearToTop(overlay: HTMLElement | null) {
  if (year.value !== null || !overlay) return;
  const listContainer = overlay.querySelector<HTMLElement>('[data-pc-section="listcontainer"]');
  const option = listContainer?.querySelector<HTMLElement>(`[data-test="${latestBirthDate.value.getFullYear()}"]`);
  if (!listContainer || !option) return;
  // Layout offsets (both relative to the absolutely positioned overlay), not bounding rects: the overlay is
  // mid scale-in animation here, and its transform would skew rect measurements
  listContainer.scrollTop = option.offsetTop - listContainer.offsetTop;
}

// Treat the two Selects as one field for blur: only emit once focus leaves the group entirely.
// The listboxes are appended inside the group, so moving between them and their Select doesn't count.
function onFocusout(event: FocusEvent) {
  const group = event.currentTarget as HTMLElement;
  if (group.contains(event.relatedTarget as Node | null)) return;
  emits('blur');
}
</script>
